import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Layout,
  Input,
  Select,
} from "antd";
import {
  FilterOutlined,
  SearchOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { getVehicleSearch } from "../api/vehicle/vehicleRequest";
import CreateVehicleButton from "../components/createVehicle/CreateVehicleButton";

import { countries, getCountryNameByCode } from "../utils/countries";
import PermissionRender from "../components/PermissionRender";

const { Content } = Layout;
const { Option } = Select;

function VehiclePage() {
  const [vehicles, setVehicles] = useState({ data: [], total: 0 });
  const [searchMatricule, setSearchMatricule] = useState("");
  const [searchModel, setSearchModel] = useState("");
  const [searchBrand, setSearchBrand] = useState("");
  const [searchColor, setSearchColor] = useState("");
  const [searchCountry, setSearchCountry] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [loading, setLoading] = useState(false);

  const fetchVehicle = async (resetPage = false) => {
    setLoading(true);
    const currentPage = resetPage ? 1 : pagination.current;
    try {
      const payload = {
        matricule: searchMatricule,
        model: searchModel,
        brand: searchBrand,
        color: searchColor,
        country: searchCountry,
        page: currentPage,
        per_page: pagination.pageSize,
      };
      const data = await getVehicleSearch(payload);
      setVehicles({
        ...data,
        data: Array.isArray(data.data) ? data.data : [],
      });
      setPagination((prev) => ({
        ...prev,
        current: data.current_page || 1,
        total: data.total || 0,
      }));
      if (resetPage && pagination.current !== 1) {
        setPagination((prev) => ({ ...prev, current: 1 }));
      } else if (data.current_page > data.last_page) {
        setPagination((prev) => ({ ...prev, current: data.last_page || 1 }));
      }
    } catch (error) {
      message.error("Échec de la récupération des données des véhicules");
      setVehicles({ data: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize]);

  const handleSearch = () => {
    fetchVehicle(true);
  };

  const getClientName = (record) => {
    if (record.owner && record.owner.name) {
      return record.owner.name;
    }
    if (record.owner && (record.owner.first_name || record.owner.last_name)) {
      return `${record?.owner?.first_name} ${record?.owner?.last_name}`;
    }
    return "Non assigné";
  };

  const columns = [
    {
      title: "Matricule",
      dataIndex: "matricule",
      key: "matricule",
    },
    {
      title: "Pays",
      dataIndex: "country",
      key: "country",
      render: (countryCode) => getCountryNameByCode(countryCode) || "-",
    },
    {
      title: "Marque",
      dataIndex: "brand",
      key: "brand",
      render: (brand) => brand || "-",
    },
    {
      title: "Modèle",
      dataIndex: "model",
      key: "model",
      render: (model) => model || "-",
    },
    {
      title: "Couleur",
      dataIndex: "color",
      key: "color",
      render: (color) => color || "-",
    },
    {
      title: "Propriétaire",
      dataIndex: "owner",
      key: "owner",
      render: (_, record) => getClientName(record),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => window.open(`/vehicle/${record.id}`, "_blank")}
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <Layout className="min-h-screen ">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={6} lg={4}>
                <Input
                  placeholder="Rechercher par Matricule"
                  prefix={<SearchOutlined />}
                  value={searchMatricule}
                  onChange={(e) => setSearchMatricule(e.target.value)}
                  onPressEnter={handleSearch}
                />
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Select
                  allowClear
                  showSearch
                  placeholder="Rechercher par Pays"
                  prefix={<GlobalOutlined />}
                  value={searchCountry}
                  onChange={(value) => setSearchCountry(value)}
                  style={{ width: "100%" }}
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onClear={() => {
                    setSearchCountry(null);
                  }}
                >
                  {countries.map((country) => (
                    <Option key={country.code} value={country.code}>
                      {country.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Input
                  placeholder="Rechercher par Marque"
                  prefix={<SearchOutlined />}
                  value={searchBrand}
                  onChange={(e) => setSearchBrand(e.target.value)}
                  onPressEnter={handleSearch}
                />
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Input
                  placeholder="Rechercher par Modèle"
                  prefix={<SearchOutlined />}
                  value={searchModel}
                  onChange={(e) => setSearchModel(e.target.value)}
                  onPressEnter={handleSearch}
                />
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Input
                  placeholder="Rechercher par Couleur"
                  prefix={<SearchOutlined />}
                  value={searchColor}
                  onChange={(e) => setSearchColor(e.target.value)}
                  onPressEnter={handleSearch}
                />
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
            <PermissionRender requiredPermission={["vehicles", "create"]}>
              <Row justify="end">
                <Col>
                  <CreateVehicleButton
                    onVehicleCreated={() => fetchVehicle(true)}
                  />
                </Col>
              </Row>
            </PermissionRender>
          </Space>
        </Card>

        <Card className="shadow-sm">
          <Table
            dataSource={vehicles.data}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              total: vehicles.total,
              current: pagination.current,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${vehicles.total || 0} véhicules`,
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize });
              },
            }}
            loading={loading}
            locale={{ emptyText: "Aucun véhicule trouvé" }}
          />
        </Card>
      </Content>
    </Layout>
  );
}

export default VehiclePage;
